import { InfoCircleOutlined } from "@ant-design/icons";
import { Card, Col, Form, message, Row, Select, Tooltip } from "antd";
import { useCallback, useEffect, useState } from "react";
import { DOMAIN_ID, isEmpty, ORG_ID } from "../../../../utils/helpers";
import { getDatabaseHostList, getDatabasesList, getDatabaseUsersList, getTableFields, getTableLists } from "../../../../service/databaseService";
import { set } from "lodash";
import { useLocation } from "react-router-dom";

const { Option } = Select;

const DatabasePolicy = ({ form, handleMaskingField, onDatabaseTypeChange }) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [notification] = message.useMessage();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isEditMode = searchParams.get("edit") === "true";
  // Selected values
  const [selectedHost, setSelectedHost] = useState(null);
  const [selectedDB, setSelectedDB] = useState(null);
  const [selectedDBUser, setSelectedDBUser] = useState(null);
  const [selectedDatabaseType, setSelectedDatabaseType] = useState(null);
  const [selectedTables, setSelectedTables] = useState([]);
  // dropdown options
  const [hostOptions, setHostOptions] = useState([]);
  const [databaseOptions, setDatabaseOptions] = useState([]);
  const [dbUserOptions, setDbUserOptions] = useState([]);
  // List
  const [databaseList, setDatabaseList] = useState([]);
  const [fieldsMap, setFieldsMap] = useState({});
  const [fieldMasking, setFieldMasking] = useState({});

  // Api calls to fetch
  const handleFetchTableFieldsSuccess = useCallback((response) => {
    if (response?.fields) {
      setFieldsMap(response.fields);
    }
  }, [setFieldsMap]);

  const handleFetchTableFieldsFailure = useCallback((error) => {
    notification.error({ message: "Error fetching table fields", description: error.message });
  }, [notification]);

  const fetchTableFields = useCallback((dbName, tables) => {
    const payload = {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
      tenantName: dbName,
      tableNames: tables,
    };
    getTableFields({ payload, onSuccess: handleFetchTableFieldsSuccess, onFailure: handleFetchTableFieldsFailure });
  }, [handleFetchTableFieldsSuccess, handleFetchTableFieldsFailure]);

  // fetch intial data like host list, database list, users etc. when component mounts

  const handleFetchHostListSuccess = (response) => {
    const hosts = response?.data || [];
    setHostOptions(hosts.map((host) => ({ value: host, label: host })));
  };
  const handleFetchHostListFailure = (error) => {
    console.error("failure fetching host list:", error?.response?.status ?? error?.status, error?.message);
  };

  const fetchHostList = useCallback(async () => {
    const payload = {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
      limit: 100,
      pageId: 1,
      filters: [],
    };
    getDatabaseHostList({ payload, onSuccess: handleFetchHostListSuccess, onFailure: handleFetchHostListFailure });
  }, []);

  const handleFetchTableListSuccess = (response) => {
    const tables = response?.table_names || [];
    // The "Select Tables" field is intentionally hidden from the UI. All table
    // names returned by getTableNames must still be sent to CreateJSONPolicy,
    // so write the full list into the form's `tables` field automatically.
    form.setFieldsValue({ tables });
  };

  const handleFetchTableListFailure = (error) => {
    console.error("failure fetching table list:", error?.response?.status ?? error?.status, error?.message);
  };

  const fetchTableList = useCallback(async () => {
    const payload = {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
      DbName: selectedDB,
    };
    getTableLists({ payload, onSuccess: handleFetchTableListSuccess, onFailure: handleFetchTableListFailure });
  }, [selectedDB]);

  const handleFetchDatabaseListSuccess = (response) => {
    const databaseList = response?.data || [];

    setDatabaseList(databaseList);
    if (databaseList.length) {
      // Carry db_type on each option so the type is known as soon as a database is selected
      const dbOptions = databaseList.map((item) => {
        return {
          value: item?.db_name,
          label: item?.db_name,
          dbType: item?.db_type || null,
        };
      });
      setDatabaseOptions(dbOptions);
    }
  };

  const handleFetchDatabaseListFailure = (error) => {
    console.error("failure fetching databases:", error?.response?.status ?? error?.status, error?.message);
  };

  const fetchDatabasesFromHost = useCallback(async () => {
    const payload = {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
      limit: 100,
      filters: [
        {
          filterParameter: "Host",
          filterValue: selectedHost,
        },
      ],
    };
    getDatabasesList({ payload, onSuccess: handleFetchDatabaseListSuccess, onFailure: handleFetchDatabaseListFailure });
  }, [selectedHost]);

  const handleFetchDatabaseUsersSuccess = (response) => {
    const usersList = response?.data || [];
    const options = usersList?.map((item) => item?.username) || [];
    if (options.length) {
      setDbUserOptions(
        options.map((user) => ({
          value: user,
          label: user,
        })),
      );
    }
  };

  const handleFetchDatabaseUsersFailure = (error) => {
    console.error("failure fetching database users:", error?.response?.status ?? error?.status, error?.message);
  };

  const fetchDatabaseUsersFromDB = useCallback(async () => {
    // Fallback for prefilled drafts/edit mode where the DB wasn't picked via the dropdown
    const dbType = databaseList?.find((db) => db?.db_name === selectedDB)?.db_type || null;
    if (dbType) {
      setSelectedDatabaseType(dbType);
      onDatabaseTypeChange?.(dbType);
    }
    fetchTableList();

    const payload = {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
      limit: 100,
      filters: [
        {
          filterParameter: "Database",
          filterValue: selectedDB,
        },
      ],
      requestId: "",
    };

    getDatabaseUsersList({ payload, onSuccess: handleFetchDatabaseUsersSuccess, onFailure: handleFetchDatabaseUsersFailure });
  }, [selectedDB, databaseList, fetchTableList, onDatabaseTypeChange]);

  // fetch data from local storage if user has already filled some data and is coming back to edit
  useEffect(() => {
    const savedFormData = isEditMode
      ? localStorage.getItem("editPolicyData")
      : localStorage.getItem("policyDraft.basicPolicyInformation");
    if (!savedFormData) return;
    let policy = null;
    try {
      policy = JSON.parse(savedFormData);
    } catch {
      return;
    }
    if (policy?.policyJson) {
      const { database } = policy.policyJson || {};
      if (database) {
        const { host, database_name, user, field_masking, privilege, tables } = database;
        field_masking && setFieldMasking(field_masking || {});
        host && setSelectedHost(host || null);
        database_name && setSelectedDB(database_name || null);
        user && setSelectedDBUser(user || null);
        tables && setSelectedTables(tables);
        form.setFieldsValue({ vm: host || null, databaseName: database_name || null, dbUser: user || null, dbPrivilege: privilege || [], tables: tables || [] });
      }
    }
  }, [isEditMode, form]);

  useEffect(() => {
    fetchHostList();
  }, [fetchHostList]);

  useEffect(() => {
    !isEmpty(selectedHost) && fetchDatabasesFromHost();
  }, [selectedHost, fetchDatabasesFromHost]);

  useEffect(() => {
    !isEmpty(selectedDB) && fetchDatabaseUsersFromDB();
  }, [selectedDB, fetchDatabaseUsersFromDB]);

  useEffect(() => {
    async function fetchData() {
      // Show loading while fetching fields
      await fetchTableFields(form.getFieldValue("databaseName"), selectedTables);
      messageApi.success({ content: "Fields loaded", key: "fetchingFields" });
    }
    if (isEmpty(selectedTables) || checkPostgres(selectedDatabaseType)) return;
    messageApi.loading({ content: "Loading table fields...", key: "fetchingFields" });
    fetchData();
  }, [selectedTables, selectedDatabaseType, messageApi, fetchTableFields, form]);

  // managae dependent dropdowns
  const handleHostChange = (value) => {
    setSelectedHost(value);
    // Reset dependent fields
    setSelectedDB(null);
    setSelectedDatabaseType(null);
    onDatabaseTypeChange?.(null);
    setSelectedDBUser(null);
    setSelectedTables([]);
    setDatabaseOptions([]);
    setDbUserOptions([]);
    setDatabaseList([]);
    setFieldsMap({});
    setFieldMasking({});
    handleMaskingField({});
    form.resetFields(["databaseName", "dbUser", "tables", "dbPrivilege", "fieldMasking"]);
  };

  const handleDatabaseChange = (value, option) => {
    setSelectedDB(value);
    // Set the database type immediately from the option (sourced from listDatabase's db_type)
    const dbType = option?.dbType || null;
    setSelectedDatabaseType(dbType);
    onDatabaseTypeChange?.(dbType);
    // Reset dependent fields
    setSelectedDBUser(null);
    setSelectedTables([]);
    setDbUserOptions([]);
    setFieldsMap({});
    setFieldMasking({});
    handleMaskingField({});
    form.resetFields(["dbUser", "tables", "dbPrivilege", "fieldMasking"]);
  };

  const handleDatabaseUserChange = (value) => {
    setSelectedDBUser(value);
    // Handle any logic needed when database user changes
  };

  // utitlity function to check if selected database is Postgres
  const checkPostgres = (dbType) => {
    return !!dbType && dbType.toLowerCase().startsWith("postgres");
  };

  // manage field masking state
  const handleFieldMaskingChange = (table, columns) => {
    setFieldMasking((prev) => ({ ...prev, [table]: columns }));
    handleMaskingField((prev) => ({ ...prev, [table]: columns })); // pass the masking fields up to parent component
  };

  return (
    <>
      {contextHolder}
      <Card
        title={
          <span>
            What Database will they get access to?{" "}
            <Tooltip title="Specify the database, tables, columns, and privileges.">
              <InfoCircleOutlined />
            </Tooltip>
          </span>
        }
        style={{ marginBottom: 16 }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Database Host" name="vm" rules={[{ required: true, message: "Please select a Host" }]}>
              <Select placeholder="Select a Host" value={selectedHost} onChange={(value) => handleHostChange(value)} options={hostOptions} allowClear />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Select Database" name="databaseName" rules={[{ required: true, message: "Please select a database" }]}>
              <Select placeholder="Select a database" value={selectedDB} onChange={handleDatabaseChange} disabled={!selectedHost} allowClear options={databaseOptions} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Database User" name="dbUser" rules={[{ required: true, message: "Please select database user" }]}>
              <Select placeholder="Select Database user" value={selectedDBUser} onChange={(value) => handleDatabaseUserChange(value)} disabled={!selectedDB} allowClear options={dbUserOptions} />
            </Form.Item>
          </Col>
          {/* "Select Tables" is intentionally hidden — all table names from
              getTableNames are auto-populated into the `tables` form field and
              sent to CreateJSONPolicy. This keeps the value in the form store
              without rendering a control. */}
          <Form.Item name="tables" hidden>
            <Select mode="multiple" />
          </Form.Item>
        </Row>

        {/* Two more fields: user & privilege */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label="Privilege" name="dbPrivilege" rules={[{ required: true, message: "Please select DB privilege" }]}>
              <Select mode="multiple" placeholder="Select privilege(s)" allowClear>
                <Option value="READ">READ</Option>
                <Option value="WRITE">WRITE</Option>
                <Option value="EXECUTE">EXECUTE</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* For each selected table, select columns to mask (not supported for Postgres) */}
        {!checkPostgres(selectedDatabaseType) && selectedTables.length > 0 && (
          <Row gutter={[16, 16]}>
            {selectedTables.map((table) => (
              <Col span={24} key={table}>
                <Form.Item
                  name="fieldMasking"
                  label={
                    <span>
                      Fields to mask in <b>{table}</b>
                    </span>
                  }
                >
                  <Select mode="multiple" placeholder={`Select fields to mask from ${table}`} value={fieldMasking[table] || []} onChange={(cols) => handleFieldMaskingChange(table, cols)} rules={[{ required: true, message: "Please select fields to mask" }]} allowClear>
                    {(fieldsMap[table] || []).map((col) => (
                      <Option key={col} value={col}>
                        {col}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            ))}
          </Row>
        )}
      </Card>
    </>
  );
};

export default DatabasePolicy;
